import { useEffect, useState } from 'react'
import { FilterSheet } from '@/components/Filters'
import { SourcePanel } from '@/components/SourcePanel'
import { DONE_WINDOW_DAYS } from '@/config'
import { loadDoneBetween } from '@/data/tasks'
import { useEventWindow, useShownEvents } from '@/hooks/useEvents'
import { usePaths } from '@/hooks/usePaths'
import { useNow } from '@/hooks/useToday'
import { useVisibleTasks } from '@/hooks/useVisibleTasks'
import { useWide } from '@/hooks/useWide'
import { periodWindow } from '@/logic/calendar'
import { addDays, fmtDate } from '@/logic/dates'
import { useTasks } from '@/store/tasks'
import { setCalendarMode, setDefaultAddContext, useUi, type CalendarMode } from '@/store/ui'
import type { DateStr, Task } from '@/types'
import { CalendarTopRow, CalendarWideRow } from './CalendarHeader'
import { DayView } from './DayView'
import { MonthPhone } from './MonthPhone'
import { MonthWide } from './MonthWide'
import { WeekGrid } from './WeekGrid'
import './calendar.css'

const NONE: Task[] = []

/**
 * One-offs ticked before the store's 30 days, read once for a period that
 * reaches back that far, so past days still show what was done.
 */
function usePastDone(from: DateStr, to: DateStr, today: DateStr): Task[] {
  const paths = usePaths()
  const cutoff = addDays(today, -DONE_WINDOW_DAYS)
  const need = from < cutoff
  const until = to < cutoff ? to : cutoff
  const [loaded, setLoaded] = useState<Task[]>(NONE)
  useEffect(() => {
    if (!need || !paths.hid) return
    let live = true
    loadDoneBetween(paths, from, until)
      .then((t) => live && setLoaded(t))
      .catch(() => live && setLoaded(NONE))
    return () => {
      live = false
    }
  }, [from, until, need, paths.hid]) // eslint-disable-line react-hooks/exhaustive-deps
  return need ? loaded : NONE
}

/**
 * The Calendar: Day, Week and Month. Phones open on Day at today and wide
 * screens on Month, each visit; the mode and the selected day live in the ui
 * store, so opening a task over the Calendar keeps both. Tasks sit where the
 * Plan tab's rules put them; Google events are read-only, coloured by whose
 * calendar they are on. The selected day is where quick add puts a new task.
 */
export function CalendarPage() {
  const wide = useWide()
  const now = useNow()
  const today = now.date
  const stored = useUi((s) => s.calendarMode)
  const mode: CalendarMode = stored ?? (wide ? 'month' : 'day')
  const date = useUi((s) => s.calendarDate)
  const ready = useTasks((s) => s.ready)
  const range = periodWindow(mode, date)
  const pastDone = usePastDone(range.from, range.to, today)
  const tasks = useVisibleTasks(pastDone)
  const events = useShownEvents()
  useEventWindow(range.from, range.to)

  // the first visit of this launch picks a mode for the screen; it is never kept on the device
  useEffect(() => {
    if (!useUi.getState().calendarMode) setCalendarMode(wide ? 'month' : 'day')
  }, [wide])

  useEffect(() => {
    setDefaultAddContext({ plannedDate: date, hint: `Add for ${fmtDate(date)}` })
  }, [date])
  useEffect(() => () => setDefaultAddContext({}), [])

  const view = !ready ? (
    <div className="cal__loading" aria-busy="true" aria-label="Loading tasks" />
  ) : mode === 'day' ? (
    <DayView tasks={tasks} events={events} now={now} date={date} wide={wide} />
  ) : mode === 'week' ? (
    <WeekGrid tasks={tasks} events={events} now={now} date={date} wide={wide} />
  ) : wide ? (
    <MonthWide tasks={tasks} events={events} today={today} date={date} />
  ) : (
    <MonthPhone tasks={tasks} events={events} now={now} date={date} />
  )

  return (
    <div className={`cal cal--${mode} ${wide ? 'cal--wide' : 'cal--phone'}`}>
      {wide ? <CalendarWideRow mode={mode} date={date} today={today} /> : <CalendarTopRow mode={mode} date={date} today={today} />}
      <div className="cal__body">{view}</div>
      <FilterSheet
        extra={
          <section className="filt__sec" aria-label="Calendars">
            <h3 className="eyebrow">Calendars</h3>
            <SourcePanel />
          </section>
        }
      />
    </div>
  )
}
